import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import {
  fetchTeamsByOwner,
  fetchAcceptedMemberships,
  fetchTeamsByIds,
  countTeamMembers,
  fetchTeamById,
  fetchTeamMembersWithProfiles,
  fetchPendingInvitations,
  insertTeam,
  insertTeamMember,
  updateTeamName,
  deleteTeam,
  updateMemberRole,
  removeTeamMember,
  acceptInvitation,
} from '@/lib/queries/teams';
import type {
  Team,
  TeamMember,
  TeamWithMembers,
  TeamRole,
  InviteMemberPayload,
} from '@/features/review/types';

const TEAMS_KEY = 'teams';
const TEAM_KEY = 'team';
const TEAM_MEMBERS_KEY = 'team-members';

/** Fetch all teams the current user is a member of (or owns) */
export function useTeams() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [TEAMS_KEY, user?.id],
    queryFn: async (): Promise<TeamWithMembers[]> => {
      if (!user) return [];

      // Get teams where user is owner
      const { data: ownedTeams, error: ownedError } = await fetchTeamsByOwner(user.id);

      if (ownedError) throw ownedError;

      // Get teams where user is a member
      const { data: memberships, error: memberError } = await fetchAcceptedMemberships(user.id);

      if (memberError) throw memberError;

      const memberTeamIds = memberships?.map((m) => m.team_id) ?? [];
      const ownedIds = (ownedTeams ?? []).map((t) => t.id);

      // Get member teams not already in owned
      const additionalIds = memberTeamIds.filter((id) => !ownedIds.includes(id));

      let memberTeams: Team[] = [];
      if (additionalIds.length > 0) {
        const { data, error } = await fetchTeamsByIds(additionalIds);

        if (error) throw error;
        memberTeams = (data ?? []) as Team[];
      }

      const allTeams = [...(ownedTeams ?? []), ...memberTeams] as Team[];

      // Fetch member counts for each team
      const teamsWithMembers: TeamWithMembers[] = await Promise.all(
        allTeams.map(async (team) => {
          const { count } = await countTeamMembers(team.id);

          return {
            ...team,
            members: [],
            member_count: count ?? 0,
          };
        }),
      );

      return teamsWithMembers;
    },
    enabled: !!user,
  });
}

/** Fetch a single team with its members */
export function useTeam(teamId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [TEAM_KEY, teamId],
    queryFn: async (): Promise<TeamWithMembers | null> => {
      if (!teamId || !user) return null;

      const { data: team, error: teamError } = await fetchTeamById(teamId);

      if (teamError) throw teamError;

      const { data: members, error: membersError } = await fetchTeamMembersWithProfiles(teamId);

      if (membersError) throw membersError;

      const membersWithProfiles: TeamMember[] = (members ?? []).map((member) => ({
        ...member,
        profile: member.profiles ?? undefined,
      })) as TeamMember[];

      return {
        ...(team as Team),
        members: membersWithProfiles,
        member_count: membersWithProfiles.length,
      };
    },
    enabled: !!teamId && !!user,
  });
}

/** Fetch team members */
export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: [TEAM_MEMBERS_KEY, teamId],
    queryFn: async (): Promise<TeamMember[]> => {
      if (!teamId) return [];

      const { data, error } = await fetchTeamMembersWithProfiles(teamId);

      if (error) throw error;

      return (data ?? []).map((member) => ({
        ...member,
        profile: member.profiles ?? undefined,
      })) as TeamMember[];
    },
    enabled: !!teamId,
  });
}

/** Create a new team */
export function useCreateTeam() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (name: string): Promise<Team> => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await insertTeam(name, user.id);

      if (error) throw error;

      // Auto-add owner as team member
      await insertTeamMember({
        team_id: data.id,
        user_id: user.id,
        role: 'owner',
        accepted_at: new Date().toISOString(),
      });

      return data as Team;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
    },
  });
}

/** Update team name */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      teamId,
      name,
    }: {
      teamId: string;
      name: string;
    }): Promise<Team> => {
      const { data, error } = await updateTeamName(teamId, name);

      if (error) throw error;
      return data as Team;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY, teamId] });
    },
  });
}

/** Delete a team */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string): Promise<void> => {
      const { error } = await deleteTeam(teamId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
    },
  });
}

/** Invite a member to a team */
export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InviteMemberPayload): Promise<TeamMember> => {
      // Use edge function to invite (bypasses RLS for profile lookup)
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-member`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            team_id: payload.team_id,
            email: payload.email,
            role: payload.role,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to invite member');
      }

      return result.member as TeamMember;
    },
    onSuccess: (_, { team_id }) => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY, team_id] });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_KEY, team_id],
      });
      toast.success('Invitation sent');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to invite member');
    },
  });
}

/** Update a member's role */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      memberId: string;
      teamId: string;
      role: TeamRole;
    }): Promise<void> => {
      const { error } = await updateMemberRole(vars.memberId, vars.role);

      if (error) throw error;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY, teamId] });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_KEY, teamId],
      });
    },
  });
}

/** Remove a member from a team */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      memberId: string;
      teamId: string;
    }): Promise<void> => {
      const { error } = await removeTeamMember(vars.memberId);

      if (error) throw error;
    },
    onSuccess: (_, { teamId }) => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY, teamId] });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_KEY, teamId],
      });
    },
  });
}

/** Accept an invitation (set accepted_at) */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (teamId: string): Promise<void> => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await acceptInvitation(teamId, user.id);

      if (error) throw error;
    },
    onSuccess: (_, teamId) => {
      queryClient.invalidateQueries({ queryKey: [TEAMS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY, teamId] });
      queryClient.invalidateQueries({
        queryKey: [TEAM_MEMBERS_KEY, teamId],
      });
      toast.success('Invitation accepted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to accept invitation');
    },
  });
}

/** Fetch pending invitations for the current user */
export function usePendingInvitations() {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['pending-invitations', user?.id],
    queryFn: async (): Promise<(TeamMember & { team?: Team })[]> => {
      if (!user) return [];

      const { data, error } = await fetchPendingInvitations(user.id);

      if (error) throw error;

      return (data ?? []).map((member) => ({
        ...member,
        team: (member.teams as Team) ?? undefined,
      })) as (TeamMember & { team?: Team })[];
    },
    enabled: !!user,
  });
}
